import { useState } from "react";
import { EditableCell } from "./EditableCell";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { fmtCurrency } from "../lib/format";
import { Plus, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";

const todayStr = () => new Date().toLocaleDateString("en-CA");

const dateInputClass =
  "h-9 rounded-md border border-input bg-white px-2 font-mono tabular-nums text-sm outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1";

/**
 * Dated payment entries log: list with inline editing + add form.
 * Entries feed the balance table's Payments total automatically.
 */
export const PaymentsSection = ({ entries, total, onAdd, onUpdate, testPrefix }) => {
  const [date, setDate] = useState(todayStr());
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);

  const parsedAmount = parseFloat(String(amount).replace(/,/g, ""));
  const canAdd = date && !isNaN(parsedAmount);

  const submit = async () => {
    if (!canAdd || adding) return;
    setAdding(true);
    try {
      await onAdd(date, parsedAmount, note.trim());
      setAmount("");
      setNote("");
      setDate(todayStr());
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <div className="w-full overflow-x-auto rounded-md border border-border">
        <Table data-testid={`${testPrefix}-payments-table`} className="min-w-[520px]">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-[160px]">Date</TableHead>
              <TableHead className="w-[160px] text-right">Amount</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="w-[110px]">By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">
                  No payments logged this week yet.
                </TableCell>
              </TableRow>
            )}
            {entries.map((e, idx) => (
              <TableRow key={e.id} className="transition-colors duration-150 hover:bg-muted/40">
                <TableCell className="p-1.5">
                  <input
                    type="date"
                    value={e.date}
                    data-testid={`${testPrefix}-payment-${idx}-date-input`}
                    onChange={(ev) => ev.target.value && onUpdate(e.id, "date", ev.target.value)}
                    className={cn(dateInputClass, "w-full")}
                  />
                </TableCell>
                <TableCell className="p-1.5">
                  <EditableCell
                    value={e.amount}
                    allowNegative
                    testId={`${testPrefix}-payment-${idx}-amount-input`}
                    onSave={(v) => onUpdate(e.id, "amount", v)}
                  />
                </TableCell>
                <TableCell className="p-1.5">
                  <EditableCell
                    type="text"
                    value={e.note}
                    placeholder="Optional note"
                    testId={`${testPrefix}-payment-${idx}-note-input`}
                    onSave={(v) => onUpdate(e.id, "note", v)}
                    className="font-sans"
                  />
                </TableCell>
                <TableCell
                  className="text-sm text-muted-foreground"
                  data-testid={`${testPrefix}-payment-${idx}-by-cell`}
                >
                  {e.created_by || "\u2014"}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="border-t-2 border-border bg-muted/40">
              <TableCell className="font-semibold">Total payments</TableCell>
              <TableCell
                data-testid={`${testPrefix}-payments-sum-cell`}
                className={cn(
                  "text-right font-mono font-semibold tabular-nums",
                  total < 0 ? "text-red-600" : "text-foreground"
                )}
              >
                {fmtCurrency(total)}
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Date</label>
          <input
            type="date"
            value={date}
            data-testid={`${testPrefix}-new-payment-date-input`}
            onChange={(e) => setDate(e.target.value)}
            className={dateInputClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Amount</label>
          <input
            value={amount}
            placeholder="0.00"
            inputMode="decimal"
            data-testid={`${testPrefix}-new-payment-amount-input`}
            onChange={(e) => setAmount(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            className={cn(dateInputClass, "w-32 text-right")}
          />
        </div>
        <div className="flex min-w-[160px] flex-1 flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">Note (optional)</label>
          <input
            value={note}
            placeholder="e.g. Wire top-up"
            data-testid={`${testPrefix}-new-payment-note-input`}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            className={cn(dateInputClass, "w-full font-sans")}
          />
        </div>
        <Button
          variant="secondary"
          onClick={submit}
          disabled={!canAdd || adding}
          data-testid={`${testPrefix}-add-payment-button`}
        >
          {adding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
          Add Payment
        </Button>
      </div>
    </div>
  );
};
